/**
 * NGHIÊN CỨU KÊNH ĐỐI THỦ — phần tính toán thuần trên số YouTube trả về: tăng trưởng giữa hai mốc,
 * view/ngày, tên nước bị chặn, tóm tắt cả kênh. Máy chủ chỉ lưu số thô; mọi con số suy ra nằm ở đây
 * để kiểm thử được.
 */
import type { CompetitorChannelDto, CompetitorSnapshotDto, CompetitorVideoDto } from '@halo/shared';
import { VIDEO_OPEN_MODE_LABELS } from '@halo/shared';
import {
  daysBetween as calendarDaysBetween,
  formatFullDate,
  weekStartOf,
  weekRangeLabel,
  type CalendarDate,
} from './dates';
import { median, normalizeForSearch } from './format';

const DAY_MS = 24 * 60 * 60 * 1000;

const daysBetween = (fromIso: string, toIso: string | Date): number =>
  ((typeof toIso === 'string' ? new Date(toIso) : toIso).getTime() - new Date(fromIso).getTime()) /
  DAY_MS;

// ---------------------------------------------------------------- nước bị chặn

let regionNames: Intl.DisplayNames | null | undefined;

/** «VN» → «Việt Nam» (theo trình duyệt); không có bảng tên thì trả nguyên mã. */
export function regionName(code: string): string {
  if (regionNames === undefined) {
    try {
      regionNames = new Intl.DisplayNames(['vi'], { type: 'region', fallback: 'none' });
    } catch {
      regionNames = null;
    }
  }
  try {
    return regionNames?.of(code) ?? code;
  } catch {
    return code;
  }
}

export interface RegionRestriction {
  /** 'blocked' = danh sách đen · 'allowed' = chỉ các nước này xem được · 'none'. */
  kind: 'blocked' | 'allowed' | 'none';
  codes: string[];
  /** «Chặn 3 nước» · «Chỉ 2 nước» · ''. */
  summary: string;
  /** Có Việt Nam trong diện không xem được không — điều nhân viên ở đây quan tâm nhất. */
  blocksVietnam: boolean;
}

export function describeRestriction(
  video: Pick<CompetitorVideoDto, 'blockedRegions' | 'allowedRegions'>,
): RegionRestriction {
  if (video.allowedRegions.length) {
    return {
      kind: 'allowed',
      codes: video.allowedRegions,
      summary: `Chỉ ${video.allowedRegions.length} nước`,
      blocksVietnam: !video.allowedRegions.includes('VN'),
    };
  }
  if (video.blockedRegions.length) {
    return {
      kind: 'blocked',
      codes: video.blockedRegions,
      summary: `Chặn ${video.blockedRegions.length} nước`,
      blocksVietnam: video.blockedRegions.includes('VN'),
    };
  }
  return { kind: 'none', codes: [], summary: '', blocksVietnam: false };
}

/**
 * TÊN CÁC NƯỚC bị chặn (hoặc được phép) thành một dòng chữ — «Việt Nam · Hoa Kỳ · Nhật Bản».
 *
 * Trước đây danh sách nước chỉ nằm trong tooltip: muốn biết video bị chặn ở đâu phải rê chuột từng
 * dòng, mà xuất Excel ra thì mất hẳn. Chủ dự án 26/09/2026 yêu cầu hiện thẳng tên nước.
 *
 * `max` = 0 nghĩa là lấy hết (file xuất ra); số dương thì cắt bớt và ghi thêm «+N» để ô trong bảng
 * không bị giãn.
 */
export function regionListText(codes: readonly string[], max = 0): string {
  const names = codes.map(regionName);
  if (max > 0 && names.length > max) {
    return `${names.slice(0, max).join(' · ')} +${names.length - max}`;
  }
  return names.join(' · ');
}

/** «Chặn: Việt Nam (VN), Hoa Kỳ (US)» · «Chỉ xem được ở: …» · '' — một ô cho file xuất ra. */
export function restrictionText(
  video: Pick<CompetitorVideoDto, 'blockedRegions' | 'allowedRegions'>,
): string {
  const restriction = describeRestriction(video);
  if (restriction.kind === 'none') return '';
  const names = restriction.codes.map((code) => `${regionName(code)} (${code})`).join(', ');
  return restriction.kind === 'allowed' ? `Chỉ xem được ở: ${names}` : `Chặn: ${names}`;
}

// ---------------------------------------------------------------- tăng trưởng

export interface Growth {
  /** Chênh lệch tuyệt đối giữa hai mốc. */
  delta: number;
  /** Số ngày giữa hai mốc (có lẻ). */
  days: number;
  /** Chênh lệch chia cho số ngày. */
  perDay: number;
  /** Phần trăm so với mốc trước; `null` khi mốc trước là 0. */
  percent: number | null;
}

function growthBetween(before: number | null, after: number | null, days: number): Growth | null {
  if (before === null || after === null || days <= 0) return null;
  const delta = after - before;
  return {
    delta,
    days,
    perDay: delta / days,
    percent: before > 0 ? (delta / before) * 100 : null,
  };
}

export interface VideoGrowth {
  views: Growth | null;
  likes: Growth | null;
  comments: Growth | null;
  /** Nhãn khoảng đo: «3,2 ngày». */
  spanLabel: string;
}

/** Tăng trưởng của một video từ lần lấy trước tới lần mới nhất; `null` khi mới lấy được một lần. */
export function videoGrowth(video: CompetitorVideoDto): VideoGrowth | null {
  if (!video.previous) return null;
  const days = daysBetween(video.previous.takenAt, video.fetchedAt);
  if (days <= 0) return null;
  return {
    views: growthBetween(video.previous.viewCount, video.viewCount, days),
    likes: growthBetween(video.previous.likeCount, video.likeCount, days),
    comments: growthBetween(video.previous.commentCount, video.commentCount, days),
    spanLabel: formatDays(days),
  };
}

/** View trung bình mỗi ngày kể từ khi đăng (tổng view ÷ tuổi video, tối thiểu 1 ngày). */
export function averageViewsPerDay(
  video: Pick<CompetitorVideoDto, 'viewCount' | 'publishedAt' | 'fetchedAt'>,
): number | null {
  if (video.viewCount === null || !video.publishedAt) return null;
  const age = Math.max(1, daysBetween(video.publishedAt, video.fetchedAt));
  return video.viewCount / age;
}

/** Tuổi video tính đến lúc lấy số, theo ngày (có lẻ). */
export function videoAgeDays(
  video: Pick<CompetitorVideoDto, 'publishedAt' | 'fetchedAt'>,
): number | null {
  return video.publishedAt ? Math.max(0, daysBetween(video.publishedAt, video.fetchedAt)) : null;
}

export interface ChannelGrowth {
  subscribers: Growth | null;
  views: Growth | null;
  videos: Growth | null;
  spanLabel: string;
}

/** Cả kênh: mốc mới nhất so với mốc liền trước. */
export function channelGrowth(
  channel: Pick<
    CompetitorChannelDto,
    'previous' | 'lastFetchedAt' | 'subscriberCount' | 'viewCount' | 'videoCount'
  >,
): ChannelGrowth | null {
  if (!channel.previous) return null;
  const days = daysBetween(channel.previous.takenAt, channel.lastFetchedAt);
  if (days <= 0) return null;
  return {
    subscribers: growthBetween(channel.previous.subscriberCount, channel.subscriberCount, days),
    views: growthBetween(channel.previous.viewCount, channel.viewCount, days),
    videos: growthBetween(channel.previous.videoCount, channel.videoCount, days),
    spanLabel: formatDays(days),
  };
}

/**
 * Tăng trưởng trong `days` ngày gần nhất: so mốc mới nhất với mốc GẦN NHẤT có tuổi ≥ `days` ngày
 * (mốc sắp theo mới nhất trước). Chưa theo dõi đủ lâu → `null`.
 */
export function growthOverDays(
  snapshots: readonly CompetitorSnapshotDto[],
  days: number,
): ChannelGrowth | null {
  const latest = snapshots[0];
  if (!latest) return null;
  const cutoff = new Date(latest.takenAt).getTime() - days * DAY_MS;
  const base = snapshots.find((snapshot) => new Date(snapshot.takenAt).getTime() <= cutoff);
  if (!base) return null;
  const span = daysBetween(base.takenAt, latest.takenAt);
  return {
    subscribers: growthBetween(base.subscriberCount, latest.subscriberCount, span),
    views: growthBetween(base.viewCount, latest.viewCount, span),
    videos: growthBetween(base.videoCount, latest.videoCount, span),
    spanLabel: formatDays(span),
  };
}

const pad2 = (value: number) => String(value).padStart(2, '0');

/** ISO → «22/09/2026» theo giờ máy người xem; chuỗi rỗng / hỏng → ''. */
export function formatIsoDate(iso: string | null | undefined): string {
  if (!iso) return '';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  return `${pad2(date.getDate())}/${pad2(date.getMonth() + 1)}/${date.getFullYear()}`;
}

/** ISO → «09:41 22/09/2026». */
export function formatIsoDateTime(iso: string | null | undefined): string {
  if (!iso) return '';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  return `${pad2(date.getHours())}:${pad2(date.getMinutes())} ${formatIsoDate(iso)}`;
}

/**
 * GIỜ VIỆT NAM, không phải giờ máy người xem (thêm 25/09/2026).
 *
 * Giờ đăng của đối thủ là một con số để HỌC: 19h tối hay 6h sáng nói lên cách họ nhắm khán giả.
 * Đọc theo đồng hồ máy thì một cái máy đặt sai múi giờ là cả bảng lệch vài tiếng mà không ai biết,
 * nên ở đây ép thẳng múi giờ Việt Nam.
 */
const VIETNAM_TIME_ZONE = 'Asia/Ho_Chi_Minh';

const vietnamFormat = new Intl.DateTimeFormat('en-GB', {
  timeZone: VIETNAM_TIME_ZONE,
  hour12: false,
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
});

export interface VietnamClock {
  day: number;
  month: number;
  year: number;
  hour: number;
  minute: number;
}

/** Tách một mốc ISO thành ngày–giờ theo giờ Việt Nam; chuỗi rỗng / hỏng → null. */
export function vietnamClock(iso: string | null | undefined): VietnamClock | null {
  if (!iso) return null;
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return null;
  const parts = new Map(
    vietnamFormat.formatToParts(date).map((part) => [part.type, Number(part.value)]),
  );
  const clock = {
    day: parts.get('day') ?? 0,
    month: parts.get('month') ?? 0,
    year: parts.get('year') ?? 0,
    hour: parts.get('hour') ?? 0,
    minute: parts.get('minute') ?? 0,
  };
  return clock.year ? clock : null;
}

/** «09:41» theo giờ Việt Nam. */
export function formatVietnamTime(iso: string | null | undefined): string {
  const clock = vietnamClock(iso);
  return clock ? `${pad2(clock.hour)}:${pad2(clock.minute)}` : '';
}

/** «09:41 · 22/09/2026» theo giờ Việt Nam — giờ ĐỨNG TRƯỚC vì đó là thứ cần đọc nhanh. */
export function formatVietnamDateTime(iso: string | null | undefined): string {
  const clock = vietnamClock(iso);
  if (!clock) return '';
  return `${pad2(clock.hour)}:${pad2(clock.minute)} · ${pad2(clock.day)}/${pad2(clock.month)}/${clock.year}`;
}

export interface PublishHour {
  /** 0–23, giờ Việt Nam. */
  hour: number;
  videos: number;
}

/**
 * Các KHUNG GIỜ kênh này hay đăng, nhiều video nhất trước. Đối thủ đăng đều một giờ thì đó là lịch
 * của họ — thứ đáng biết hơn từng mốc giờ lẻ.
 */
export function publishHours(
  videos: readonly Pick<CompetitorVideoDto, 'publishedAt'>[],
): PublishHour[] {
  const counts = new Map<number, number>();
  for (const video of videos) {
    const clock = vietnamClock(video.publishedAt);
    if (!clock) continue;
    counts.set(clock.hour, (counts.get(clock.hour) ?? 0) + 1);
  }
  return [...counts.entries()]
    .map(([hour, count]) => ({ hour, videos: count }))
    .sort((a, b) => b.videos - a.videos || a.hour - b.hour);
}

/** «vừa xong» · «25 phút trước» · «3 giờ trước» · «2 ngày trước» — cho «cập nhật lúc». */
export function formatAgo(iso: string, now: Date): string {
  const minutes = Math.round((now.getTime() - new Date(iso).getTime()) / 60_000);
  if (minutes < 1) return 'vừa xong';
  if (minutes < 60) return `${minutes} phút trước`;
  const hours = Math.round(minutes / 60);
  if (hours < 48) return `${hours} giờ trước`;
  return `${Math.round(hours / 24)} ngày trước`;
}

/** «0,5 ngày» · «3 ngày» · «12,5 ngày». */
export function formatDays(days: number): string {
  const rounded = days >= 10 ? Math.round(days) : Math.round(days * 10) / 10;
  return `${new Intl.NumberFormat('vi-VN', { maximumFractionDigits: 1 }).format(rounded)} ngày`;
}

// ---------------------------------------------------------------- doanh thu ƯỚC TÍNH

/**
 * RPM mặc định khi Giám đốc chưa đặt: 0,50 $ / 1.000 view.
 *
 * Con số này là MỘT PHỎNG ĐOÁN, không phải số thật của đối thủ — YouTube không công khai doanh thu
 * kênh người khác. Nó chỉ để so kênh này với kênh kia trên cùng một thước, nên quan trọng là cả
 * công ty dùng CHUNG một con số và biết rõ nó ở đâu ra (Cài đặt › Ngưỡng bôi màu).
 */
export const DEFAULT_COMPETITOR_RPM = 0.5;

/** Doanh thu ước tính (USD) của `views` lượt xem theo RPM đã đặt. */
export function estimatedRevenue(views: number | null, rpm: number): number | null {
  if (views === null || !Number.isFinite(views) || views < 0) return null;
  if (!Number.isFinite(rpm) || rpm <= 0) return null;
  return (views / 1000) * rpm;
}

export interface RevenueEstimate {
  /** Tổng view của các video ĐANG GIỮ trong hệ thống. */
  fetchedViews: number;
  /** Ước tính theo số view ấy. */
  fetchedUsd: number | null;
  /** Ước tính theo TỔNG view cả kênh YouTube công bố (gồm cả video ta chưa lấy về). */
  channelUsd: number | null;
}

/**
 * Ước tính cho cả kênh: một con số theo phần video đang giữ (so sánh được giữa các kênh vì cùng
 * một phép đếm), một con số theo tổng view cả đời kênh.
 */
export function estimateChannelRevenue(
  videos: readonly Pick<CompetitorVideoDto, 'viewCount'>[],
  channelViews: number,
  rpm: number,
): RevenueEstimate {
  const fetchedViews = videos.reduce((sum, video) => sum + (video.viewCount ?? 0), 0);
  return {
    fetchedViews,
    fetchedUsd: estimatedRevenue(fetchedViews, rpm),
    channelUsd: estimatedRevenue(channelViews, rpm),
  };
}

// ---------------------------------------------------------------- tóm tắt cả kênh

export interface VideoSummary {
  total: number;
  removed: number;
  /** Video đăng trong 30 ngày trước lần lấy số mới nhất. */
  last30Days: number;
  /** Video đăng trong 7 ngày. */
  last7Days: number;
  averageViews: number | null;
  medianViews: number | null;
  /** Tổng view của các video đang giữ (không tính video đã gỡ). */
  totalViews: number;
  /** like ÷ view, phần trăm, trên video có cả hai số. */
  likeRate: number | null;
  /** bình luận ÷ view, phần trăm. */
  commentRate: number | null;
  /** Video có chặn nước (danh sách đen hoặc trắng). */
  restricted: number;
  /** Video Việt Nam không xem được. */
  blocksVietnam: number;
  /** Video trung bình mỗi tuần, tính trên 90 ngày gần nhất. */
  uploadsPerWeek: number | null;
  /** Video nhiều view nhất đang giữ. */
  top: CompetitorVideoDto | null;
}

export function summarizeVideos(
  videos: readonly CompetitorVideoDto[],
  fetchedAt: string | Date,
): VideoSummary {
  const kept = videos.filter((video) => !video.removedAt);
  const withViews = kept.filter((video) => video.viewCount !== null);
  const views = withViews.map((video) => video.viewCount as number);
  const ageOf = (video: CompetitorVideoDto) =>
    video.publishedAt ? daysBetween(video.publishedAt, fetchedAt) : Infinity;
  const within = (days: number) => kept.filter((video) => ageOf(video) <= days).length;
  const likeVideos = withViews.filter(
    (video) => video.likeCount !== null && (video.viewCount as number) > 0,
  );
  const commentVideos = withViews.filter(
    (video) => video.commentCount !== null && (video.viewCount as number) > 0,
  );
  const rate = (list: CompetitorVideoDto[], pick: (video: CompetitorVideoDto) => number | null) => {
    if (!list.length) return null;
    const totalViews = list.reduce((sum, video) => sum + (video.viewCount as number), 0);
    const total = list.reduce((sum, video) => sum + (pick(video) ?? 0), 0);
    return totalViews > 0 ? (total / totalViews) * 100 : null;
  };
  const last90 = within(90);
  const oldest = kept.reduce(
    (max, video) => Math.max(max, ageOf(video) === Infinity ? 0 : ageOf(video)),
    0,
  );
  // Kênh trẻ hơn 90 ngày thì chia theo tuổi thật, không chia cho 90.
  const weeks = Math.min(90, oldest) / 7;
  return {
    total: kept.length,
    removed: videos.length - kept.length,
    last30Days: within(30),
    last7Days: within(7),
    averageViews: views.length ? views.reduce((sum, value) => sum + value, 0) / views.length : null,
    medianViews: median(views),
    totalViews: views.reduce((sum, value) => sum + value, 0),
    likeRate: rate(likeVideos, (video) => video.likeCount),
    commentRate: rate(commentVideos, (video) => video.commentCount),
    restricted: kept.filter((video) => describeRestriction(video).kind !== 'none').length,
    blocksVietnam: kept.filter((video) => describeRestriction(video).blocksVietnam).length,
    uploadsPerWeek: weeks >= 1 ? last90 / weeks : null,
    top: withViews.length
      ? withViews.reduce((best, video) =>
          (video.viewCount ?? 0) > (best.viewCount ?? 0) ? video : best,
        )
      : null,
  };
}

// ---------------------------------------------------------------- lọc

export interface VideoFilter {
  query: string;
  onlyRestricted: boolean;
  includeRemoved: boolean;
}

/**
 * Khoảng thời gian lọc video của bảng: 7 ngày · 30 ngày · toàn thời gian, hoặc ĐÚNG MỘT THÁNG
 * («m:09/2026») hay ĐÚNG MỘT TUẦN («w:20/09/2026», khoá là ngày Chủ nhật) — cả hai thêm 26/09/2026.
 *
 * Ba mốc đầu trả lời «gần đây họ ra gì»; tháng trả lời «tháng 8 họ làm được bao nhiêu»; còn TUẦN là
 * nhịp báo cáo của công ty — bảng Thị trường cũng xếp theo tuần Chủ nhật → Thứ 7, nên soi đối thủ
 * theo đúng tuần ấy thì hai bảng nói cùng một ngôn ngữ.
 *
 * «7 ngày» KHÔNG thay được tuần: nó là bảy ngày tính lùi từ hôm nay, trượt theo từng ngày.
 */
export type VideoRange = '7d' | '30d' | 'all' | `m:${string}` | `w:${string}`;

export const VIDEO_RANGES: { value: VideoRange; label: string; days: number | null }[] = [
  { value: '7d', label: '7 ngày', days: 7 },
  { value: '30d', label: '30 ngày', days: 30 },
  { value: 'all', label: 'Toàn thời gian', days: null },
];

/** «09/2026» → khoảng lọc của riêng tháng ấy. */
export const monthRange = (monthKey: string): VideoRange => `m:${monthKey}`;

/** «20/09/2026» (ngày Chủ nhật) → khoảng lọc của riêng tuần ấy. */
export const weekRange = (weekStartKey: string): VideoRange => `w:${weekStartKey}`;

/** Khoảng đang lọc là một tuần thì trả «DD/MM/YYYY» của Chủ nhật, không thì trả ''. */
export const rangeWeek = (range: VideoRange): string =>
  range.startsWith('w:') ? range.slice(2) : '';

/** Ngày đăng của video theo GIỜ VIỆT NAM, dạng lịch — để xếp vào tuần / tháng. */
function vietnamDate(publishedAt: string | null | undefined): CalendarDate | null {
  const clock = vietnamClock(publishedAt);
  return clock ? { day: clock.day, month: clock.month, year: clock.year } : null;
}

/** Chủ nhật mở đầu tuần chứa video ấy, «DD/MM/YYYY»; '' = chưa rõ ngày đăng. */
export function videoWeekKey(publishedAt: string | null | undefined): string {
  const date = vietnamDate(publishedAt);
  return date ? formatFullDate(weekStartOf(date)) : '';
}

/** Khoảng đang lọc là một tháng thì trả «MM/YYYY», không thì trả ''. */
export const rangeMonth = (range: VideoRange): string =>
  range.startsWith('m:') ? range.slice(2) : '';

/** Nhãn ngắn của khoảng, để viết vào dòng tổng và tên file xuất ra. */
export function rangeLabel(range: VideoRange): string {
  const month = rangeMonth(range);
  if (month) return `tháng ${month}`;
  const week = rangeWeek(range);
  if (week) {
    const weekStart = parseDayMonthYear(week);
    return weekStart ? `tuần ${weekRangeLabel(weekStart)}` : 'tuần';
  }
  return VIDEO_RANGES.find((item) => item.value === range)?.label.toLowerCase() ?? '';
}

/** «DD/MM/YYYY» → ngày lịch; chuỗi hỏng → null. */
function parseDayMonthYear(text: string): CalendarDate | null {
  const [day, month, year] = text.split('/').map(Number);
  if (!day || !month || !year) return null;
  return { day, month, year };
}

/** Số ngày của tháng «MM/YYYY»; null nếu chuỗi tháng hỏng. Tự biết năm nhuận. */
function daysInMonth(monthKey: string): number | null {
  const [month, year] = monthKey.split('/').map(Number);
  if (!month || !year || month < 1 || month > 12) return null;
  return new Date(Date.UTC(year, month, 0)).getUTCDate();
}

/**
 * Khoảng này dài bao nhiêu ngày — để chia ra «video/tuần». `null` = không đếm được (toàn thời gian).
 *
 * THÁNG ĐANG CHẠY chỉ tính số ngày ĐÃ QUA: tháng 9 mới tới ngày 26 mà chia cho 30 thì nhịp đăng của
 * họ trông thưa hơn thực tế.
 */
export function rangeDays(range: VideoRange, now: Date): number | null {
  const week = rangeWeek(range);
  if (week) {
    const weekStart = parseDayMonthYear(week);
    if (!weekStart) return null;
    const clock = vietnamClock(now.toISOString());
    if (!clock) return 7;
    // TUẦN ĐANG CHẠY chỉ tính số ngày ĐÃ QUA — cùng lẽ với tháng đang chạy.
    const passed =
      calendarDaysBetween(weekStart, { day: clock.day, month: clock.month, year: clock.year }) + 1;
    return passed >= 1 && passed < 7 ? passed : 7;
  }
  const month = rangeMonth(range);
  if (month) {
    const total = daysInMonth(month);
    if (total === null) return null;
    const clock = vietnamClock(now.toISOString());
    const here = clock ? `${pad2(clock.month)}/${clock.year}` : '';
    return here === month ? Math.max(1, Math.min(total, clock?.day ?? total)) : total;
  }
  return VIDEO_RANGES.find((item) => item.value === range)?.days ?? null;
}

export interface VideoMonth {
  /** «09/2026». */
  key: string;
  /** «Tháng 09/2026». */
  label: string;
  videos: number;
}

/**
 * Các THÁNG có video, mới nhất trước — dựng thẳng từ dữ liệu nên không bao giờ mời người ta chọn
 * một tháng trống. Video chưa rõ ngày đăng không tạo ra tháng nào.
 */
export function videoMonths(videos: readonly CompetitorVideoDto[]): VideoMonth[] {
  const counts = new Map<string, number>();
  for (const video of videos) {
    const key = videoMonthKey(video.publishedAt);
    if (key) counts.set(key, (counts.get(key) ?? 0) + 1);
  }
  return [...counts.entries()]
    .sort(([a], [b]) => monthOrdinal(b) - monthOrdinal(a))
    .map(([key, count]) => ({ key, label: `Tháng ${key}`, videos: count }));
}

/** «09/2026» → 202609, để xếp tháng. */
const monthOrdinal = (monthKey: string): number => {
  const [month, year] = monthKey.split('/').map(Number);
  return (year || 0) * 100 + (month || 0);
};

export interface VideoWeek {
  /** «20/09/2026» — ngày Chủ nhật. */
  key: string;
  /** «21/09–27/09». */
  label: string;
  videos: number;
}

/**
 * Các TUẦN có video, mới nhất trước — dựng thẳng từ dữ liệu như danh sách tháng, nên không bao giờ
 * mời người ta chọn một tuần trống.
 */
export function videoWeeks(videos: readonly CompetitorVideoDto[]): VideoWeek[] {
  const counts = new Map<string, number>();
  for (const video of videos) {
    const key = videoWeekKey(video.publishedAt);
    if (key) counts.set(key, (counts.get(key) ?? 0) + 1);
  }
  return [...counts.entries()]
    .sort(([a], [b]) => dayMonthYearOrdinal(b) - dayMonthYearOrdinal(a))
    .map(([key, count]) => {
      const weekStart = parseDayMonthYear(key);
      return { key, label: weekStart ? weekRangeLabel(weekStart) : key, videos: count };
    });
}

/** «21/09/2026» → 20260921, để xếp tuần. */
const dayMonthYearOrdinal = (text: string): number => {
  const date = parseDayMonthYear(text);
  return date ? (date.year * 100 + date.month) * 100 + date.day : 0;
};

/**
 * Video ĐĂNG trong khoảng vừa qua, tính từ `now` (đồng hồ ứng dụng) chứ không từ lần lấy số gần
 * nhất: người ta hỏi «tuần vừa rồi họ ra mấy bài», mốc phải là hôm nay. Kênh lâu chưa cập nhật thì
 * ra ít video — đó cũng là một câu trả lời đúng.
 *
 * Video chưa rõ ngày đăng (dữ liệu hỏng) coi như KHÔNG thuộc khoảng nào, trừ «toàn thời gian».
 *
 * Lọc theo THÁNG thì so đúng tháng đăng (giờ Việt Nam), không liên quan hôm nay là ngày nào.
 */
export function videosInRange<T extends Pick<CompetitorVideoDto, 'publishedAt'>>(
  videos: readonly T[],
  range: VideoRange,
  now: Date,
): T[] {
  const week = rangeWeek(range);
  if (week) return videos.filter((video) => videoWeekKey(video.publishedAt) === week);
  const month = rangeMonth(range);
  if (month) return videos.filter((video) => videoMonthKey(video.publishedAt) === month);
  const days = VIDEO_RANGES.find((item) => item.value === range)?.days ?? null;
  if (days === null) return [...videos];
  const from = now.getTime() - days * 86_400_000;
  return videos.filter((video) => {
    const at = new Date(video.publishedAt).getTime();
    return Number.isFinite(at) && at >= from;
  });
}

export interface RangeTotals {
  videos: number;
  views: number;
  likes: number;
  comments: number;
  /** View trung bình mỗi video trong khoảng; null khi không có video nào. */
  viewsPerVideo: number | null;
  /** Video mỗi tuần trong khoảng; null với «toàn thời gian» (không có số ngày để chia). */
  perWeek: number | null;
  usd: number | null;
}

/**
 * TỔNG CỘNG của khoảng đang lọc (thêm 25/09/2026): bảng liệt kê từng video, còn đây là con số để
 * so kênh này với kênh kia — «bảy ngày qua họ ra 5 bài, gom 2,1 triệu view».
 *
 * Like và bình luận CỘNG DỒN chỉ trên video có số: kênh giấu like thì ô ấy là `null`, cộng nó
 * thành 0 là bịa ra một con số thấp hơn sự thật.
 *
 * `now` chỉ dùng khi khoảng là MỘT THÁNG, để biết tháng ấy đã qua mấy ngày — xem `rangeDays`.
 */
export function rangeTotals(
  videos: readonly CompetitorVideoDto[],
  range: VideoRange,
  rpm: number,
  now: Date = new Date(),
): RangeTotals {
  const days = rangeDays(range, now);
  const sum = (pick: (video: CompetitorVideoDto) => number | null) =>
    videos.reduce((total, video) => total + (pick(video) ?? 0), 0);
  const views = sum((video) => video.viewCount);
  return {
    videos: videos.length,
    views,
    likes: sum((video) => video.likeCount),
    comments: sum((video) => video.commentCount),
    viewsPerVideo: videos.length ? Math.round(views / videos.length) : null,
    perWeek: days === null ? null : (videos.length / days) * 7,
    usd: estimatedRevenue(views, rpm),
  };
}

export function filterVideos(
  videos: readonly CompetitorVideoDto[],
  filter: VideoFilter,
): CompetitorVideoDto[] {
  const needle = normalizeForSearch(filter.query);
  return videos.filter((video) => {
    if (!filter.includeRemoved && video.removedAt) return false;
    if (filter.onlyRestricted && describeRestriction(video).kind === 'none') return false;
    if (needle && !normalizeForSearch(video.title).includes(needle)) return false;
    return true;
  });
}

/** Link mở kênh / video trên YouTube. */
export const youtubeChannelUrl = (channel: Pick<CompetitorChannelDto, 'youtubeId' | 'handle'>) =>
  channel.handle
    ? `https://www.youtube.com/${channel.handle}`
    : `https://www.youtube.com/channel/${channel.youtubeId}`;
export const youtubeVideoUrl = (video: Pick<CompetitorVideoDto, 'youtubeId'>) =>
  `https://www.youtube.com/watch?v=${video.youtubeId}`;

// ---------------------------------------------------------------- xuất Excel

/**
 * THÁNG ĐĂNG của một video, «MM/YYYY» theo GIỜ VIỆT NAM (26/09/2026).
 *
 * Phải theo giờ Việt Nam chứ không theo giờ máy: video đăng 6 giờ sáng ngày 1 giờ Việt Nam là
 * 23 giờ ngày 30 tháng trước theo giờ UTC — xếp nhầm tháng thì bảng tổng hợp lệch hẳn một kỳ.
 */
export function videoMonthKey(publishedAt: string | null | undefined): string {
  const clock = vietnamClock(publishedAt);
  if (!clock) return '';
  return `${pad2(clock.month)}/${clock.year}`;
}

/**
 * HÌNH THỨC MỞ VIDEO thành chữ cho bảng và cho file Excel. Chưa rõ thì để TRỐNG chứ không ghi
 * «công khai» — dòng lấy về trước 27/09/2026 chưa có thông tin này (xem `VideoOpenMode`).
 */
export function openModeText(video: CompetitorVideoDto): string {
  return video.openMode ? VIDEO_OPEN_MODE_LABELS[video.openMode] : '';
}

export const VIDEO_EXPORT_HEADER = [
  'Tháng',
  'Ngày đăng',
  'Giờ đăng',
  'Hình thức mở',
  'Tiêu đề',
  'Link video',
  'Lượt xem',
  'Like',
  'Bình luận',
  'Ước tính $',
  'Nước bị chặn',
  'Tình trạng',
] as const;

/**
 * Danh sách video thành bảng để mở bằng Excel — mỗi video một dòng, có cột THÁNG để lọc / gộp
 * theo tháng ngay trong Excel (chủ dự án 26/09/2026).
 *
 * Xếp THÁNG MỚI NHẤT TRƯỚC, trong tháng thì video mới trước: mở file ra là thấy ngay tháng này họ
 * ra những gì. Video chưa rõ ngày đăng dồn xuống cuối chứ không đoán bừa vào tháng nào.
 */
export function videoExportRows(videos: readonly CompetitorVideoDto[], rpm: number): string[][] {
  const ordinal = (video: CompetitorVideoDto) => {
    const clock = vietnamClock(video.publishedAt);
    if (!clock) return -1;
    return (
      ((clock.year * 100 + clock.month) * 100 + clock.day) * 10_000 +
      clock.hour * 100 +
      clock.minute
    );
  };
  return [...videos]
    .sort((a, b) => ordinal(b) - ordinal(a))
    .map((video) => {
      const clock = vietnamClock(video.publishedAt);
      const usd = estimatedRevenue(video.viewCount, rpm);
      return [
        videoMonthKey(video.publishedAt),
        clock ? `${pad2(clock.day)}/${pad2(clock.month)}/${clock.year}` : '',
        clock ? `${pad2(clock.hour)}:${pad2(clock.minute)}` : '',
        openModeText(video),
        video.title,
        youtubeVideoUrl(video),
        video.viewCount === null ? '' : String(video.viewCount),
        video.likeCount === null ? '' : String(video.likeCount),
        video.commentCount === null ? '' : String(video.commentCount),
        usd === null ? '' : usd.toFixed(2),
        restrictionText(video),
        video.removedAt ? 'đã gỡ' : '',
      ];
    });
}

/** Tổng theo TỪNG THÁNG: số video, lượt xem, ước tính $ — tháng mới nhất trước. */
export const VIDEO_MONTH_HEADER = ['Tháng', 'Số video', 'Lượt xem', 'Ước tính $'] as const;

export function videoMonthRows(videos: readonly CompetitorVideoDto[], rpm: number): string[][] {
  const months = new Map<string, { videos: number; views: number }>();
  for (const video of videos) {
    const key = videoMonthKey(video.publishedAt);
    const month = months.get(key) ?? { videos: 0, views: 0 };
    month.videos += 1;
    month.views += video.viewCount ?? 0;
    months.set(key, month);
  }
  return [...months.entries()]
    .sort(([a], [b]) => {
      if (!a) return 1;
      if (!b) return -1;
      const [monthA, yearA] = a.split('/');
      const [monthB, yearB] = b.split('/');
      return Number(yearB) * 100 + Number(monthB) - (Number(yearA) * 100 + Number(monthA));
    })
    .map(([key, month]) => {
      const usd = estimatedRevenue(month.views, rpm);
      return [
        key || 'chưa rõ tháng',
        String(month.videos),
        String(month.views),
        usd === null ? '' : usd.toFixed(2),
      ];
    });
}

/** Lọc danh sách kênh đã lưu theo tên / handle. */
export function filterChannels(
  channels: readonly CompetitorChannelDto[],
  query: string,
): CompetitorChannelDto[] {
  const needle = normalizeForSearch(query);
  if (!needle) return [...channels];
  return channels.filter((channel) =>
    normalizeForSearch(`${channel.title} ${channel.handle}`).includes(needle),
  );
}

/** Nhập nhiều kênh một lượt (09/10/2026): tối đa chừng này link mỗi lần dán. */
export const BULK_CHANNEL_MAX = 50;

/**
 * Tách danh sách link kênh dán vào (chủ dự án 09/10/2026: «chức năng nhập nhiều link kênh 1 lần»): mỗi dòng / dấu
 * cách / dấu phẩy một link; bỏ số thứ tự «1.» và gạch đầu dòng; bỏ trùng (không phân biệt hoa thường, bỏ «/» cuối).
 */
export function parseChannelLinks(text: string): string[] {
  const seen = new Set<string>();
  const links: string[] = [];
  for (const raw of text.split(/[\s,;]+/)) {
    const link = raw
      .replace(/^[-*•]+/, '')
      .replace(/^\d+[.)]$/, '')
      .trim();
    if (!link) continue;
    const key = link.toLowerCase().replace(/\/+$/, '');
    if (seen.has(key)) continue;
    seen.add(key);
    links.push(link);
  }
  return links;
}
